import { Component } from './component.js';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends {Component<Refs>}
 */
class HomepageArtifactShowcase extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();

    this.refs.tabs?.forEach((tab) => {
      tab.addEventListener('click', () => this.#activate(tab, true), {
        signal: this.#abortController.signal,
      });
      tab.addEventListener('keydown', (event) => this.#handleKeydown(event), {
        signal: this.#abortController.signal,
      });
    });

    const selectedTab = this.refs.tabs?.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (selectedTab) this.#activate(selectedTab, false);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const currentIndex = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    if (currentIndex < 0) return;

    const lastIndex = tabs.length - 1;
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
        break;
      case 'ArrowRight':
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(tabs[nextIndex], true);
  }

  /**
   * @param {HTMLButtonElement} activeTab
   * @param {boolean} moveFocus
   */
  #activate(activeTab, moveFocus) {
    const activePanelId = activeTab.getAttribute('aria-controls');
    const activeVariant = activeTab.dataset.panel;
    const panels = this.refs.panels ?? [];
    const activePanel = panels.find((panel) => panel.id === activePanelId);
    if (!activePanel) return;

    this.refs.tabs?.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      if (panel === activePanel) {
        panel.hidden = false;
        panel.dataset.transition = 'active';
        return;
      }

      panel.hidden = true;
      delete panel.dataset.transition;
    });

    if (activeVariant) this.dataset.activePanel = activeVariant;
    if (moveFocus) activeTab.focus();
  }
}

if (!customElements.get('homepage-artifact-showcase')) {
  customElements.define('homepage-artifact-showcase', HomepageArtifactShowcase);
}
